<template>
  <div class="conversation-list">
    <!-- Header -->
    <div class="list-header">
      <div class="header-row" :class="{ 'with-recipient': showRecipient }">
        <div class="column-header participants">
          {{ showRecipient ? "Participants" : "From" }}
        </div>
        <div class="column-header subject">Subject</div>
        <div class="column-header message-count">Messages</div>
        <div class="column-header date">Last Message</div>
        <div class="column-header actions">Actions</div>
      </div>
    </div>

    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <svg class="spinner" viewBox="0 0 50 50">
        <circle
          class="path"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke-width="4"
        ></circle>
      </svg>
      <p>Loading conversations...</p>
    </div>

    <!-- Empty state -->
    <div v-else-if="conversations.length === 0" class="empty-state">
      <div class="empty-icon">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
          ></path>
          <polyline points="22,6 12,13 2,6"></polyline>
        </svg>
      </div>
      <h3>No conversations found</h3>
      <p>There are no conversations in this folder yet.</p>
    </div>

    <!-- Conversation list -->
    <div v-else class="list-body">
      <div
        v-for="conversation in conversations"
        :key="conversation.id"
        class="conversation-row"
        :class="{
          unread: conversation.unread_count > 0,
          'has-attachments': hasAttachments(conversation),
          'with-recipient': showRecipient,
        }"
      >
        <!-- From column -->
        <div class="column-content participants">
          <div
            class="participants-info"
            @click="handleConversationClick(conversation.id)"
            @click.stop
          >
            <!-- From (sender) -->
            <div class="participant-row from-row">
              <div class="participant-label">From:</div>
              <div class="participant-content">
                <div
                  class="participant-avatar"
                  :style="{
                    backgroundColor: getEmailColor(
                      getLatestSender(conversation)
                    ),
                  }"
                >
                  {{ getEmailInitials(getLatestSender(conversation)) }}
                </div>
                <div class="participant-name">
                  {{ getLatestSender(conversation) }}
                </div>
              </div>
            </div>

            <!-- To (recipient) - only shown in dashboard -->
            <div v-if="showRecipient" class="participant-row to-row">
              <div class="participant-label">To:</div>
              <div class="participant-content">
                <div
                  class="participant-avatar"
                  :style="{
                    backgroundColor: getEmailColor(
                      getRecipientEmail(conversation)
                    ),
                  }"
                >
                  {{ getEmailInitials(getRecipientEmail(conversation)) }}
                </div>
                <div class="participant-name">
                  {{ getRecipientEmail(conversation) }}
                </div>
              </div>
            </div>

            <!-- Multiple participants indicator -->
            <div
              v-if="!showRecipient && conversation.participants_list.length > 2"
              class="participants-count"
            >
              +{{ conversation.participants_list.length - 1 }} others
            </div>
          </div>
        </div>

        <!-- Subject column -->
        <div class="column-content subject">
          <div class="subject-line">
            <span class="subject-text">{{
              conversation.clean_subject || conversation.subject
            }}</span>
            <div class="subject-meta">
              <span
                v-if="conversation.unread_count > 0"
                class="unread-indicator"
              >
                New
              </span>
              <span
                v-if="hasAttachments(conversation)"
                class="attachment-indicator"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path
                    d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.49"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div class="latest-content">{{ getLatestContent(conversation) }}</div>
        </div>

        <!-- Message count column -->
        <div class="column-content message-count">
          <div class="count-badge">
            {{ conversation.message_count }}
          </div>
        </div>

        <!-- Date column -->
        <div class="column-content date">
          <div class="date-info">
            <div class="date-text">
              {{ formatDate(conversation.last_message_at) }}
            </div>
            <div class="time-text">
              {{ formatTime(conversation.last_message_at) }}
            </div>
          </div>
        </div>

        <!-- Actions column -->
        <div class="column-content actions">
          <div class="action-buttons">
            <button
              v-if="conversation.unread_count > 0"
              @click.stop="handleMarkAsRead(conversation.id)"
              class="action-button"
              title="Mark as read"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
            </button>
            <button
              @click.stop="handleDelete(conversation.id)"
              class="action-button delete-button"
              title="Delete conversation"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <polyline points="3,6 5,6 21,6"></polyline>
                <path
                  d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2"
                ></path>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ConversationList",
  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    showRecipient: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["conversation-click", "delete-conversation", "mark-read", "refresh"],
  methods: {
    handleConversationClick(conversationId) {
      this.$emit("conversation-click", conversationId);
    },

    handleMarkAsRead(conversationId) {
      this.$emit("mark-read", conversationId);
    },

    handleDelete(conversationId) {
      if (confirm("Are you sure you want to delete this conversation?")) {
        this.$emit("delete-conversation", conversationId);
      }
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        return "Today";
      } else if (diffDays === 2) {
        return "Yesterday";
      } else if (diffDays <= 7) {
        return date.toLocaleDateString("en-US", { weekday: "short" });
      } else {
        return date.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
    },

    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
    },

    getEmailColor(emailAddress) {
      if (!emailAddress) return "#3b82f6";
      const colors = [
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#f97316",
        "#14b8a6",
        "#f59e0b",
        "#10b981",
        "#6366f1",
        "#ef4444",
        "#84cc16",
      ];
      let hash = 0;
      for (let i = 0; i < emailAddress.length; i++) {
        hash = emailAddress.charCodeAt(i) + ((hash << 5) - hash);
      }
      return colors[Math.abs(hash) % colors.length];
    },

    getEmailInitials(emailAddress) {
      if (!emailAddress) return "??";
      const localPart = emailAddress.split("@")[0];
      return localPart.slice(0, 2).toUpperCase();
    },

    getLatestSender(conversation) {
      // Use the latest_message data from the backend if available
      if (conversation.latest_message && conversation.latest_message.sender) {
        return conversation.latest_message.sender;
      }
      // Fallback to first participant
      if (
        conversation.participants_list &&
        conversation.participants_list.length > 0
      ) {
        return conversation.participants_list[0];
      }
      return "Unknown";
    },

    getRecipientEmail(conversation) {
      // Try to get the recipient email from the conversation data
      if (conversation.email_address) {
        return conversation.email_address;
      }
      // Alternative: try to get it from the latest message
      if (
        conversation.latest_message &&
        conversation.latest_message.recipient
      ) {
        return conversation.latest_message.recipient;
      }
      // Fallback: try to get it from participants (exclude sender)
      if (
        conversation.participants_list &&
        conversation.participants_list.length > 1
      ) {
        const sender = this.getLatestSender(conversation);
        const recipient = conversation.participants_list.find(
          (p) => p !== sender
        );
        if (recipient) {
          return recipient;
        }
      }
      return "Unknown";
    },

    getLatestContent(conversation) {
      // Use the latest_message data from the backend if available
      if (conversation.latest_message && conversation.latest_message.content) {
        return conversation.latest_message.content;
      }
      // Fallback to message count display
      return `${conversation.message_count} message${
        conversation.message_count !== 1 ? "s" : ""
      } in this conversation`;
    },

    hasAttachments(conversation) {
      // Check if the latest message has attachments
      if (
        conversation.latest_message &&
        conversation.latest_message.has_attachments
      ) {
        return true;
      }
      // You could also check if any message in the conversation has attachments
      // but for now we'll just check the latest message
      return false;
    },
  },
};
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #3b82f6;
$primary-hover: #2563eb;
$success-color: #10b981;
$error-color: #ef4444;
$text-primary: #111827;
$text-secondary: #4b5563;
$text-muted: #6b7280;
$text-light: #9ca3af;
$bg-white: #ffffff;
$bg-light: #f9fafb;
$bg-hover: #e5e7eb;
$border-color: #e5e7eb;
$border-dark: #d1d5db;
$shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
$shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
$radius: 0.375rem;
$transition: all 0.2s ease;

.conversation-list {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: $bg-white;
  border-radius: $radius;
  overflow: hidden;
  box-shadow: $shadow;
  margin-top: 0.5rem;

  // Header
  .list-header {
    background: linear-gradient(135deg, $bg-light, $bg-white);
    border-bottom: 2px solid $border-color;
    padding: 0.5rem 0.75rem;

    .header-row {
      display: grid;
      grid-template-columns: 200px 1fr 80px 120px 80px;
      gap: 0.75rem;
      align-items: center;

      &.with-recipient {
        grid-template-columns: 240px 1fr 70px 110px 70px;

        @media (max-width: 1024px) {
          grid-template-columns: 200px 1fr 60px;
          .message-count,
          .actions {
            display: none;
          }
        }

        @media (max-width: 768px) {
          grid-template-columns: 1fr 80px;
          .message-count,
          .actions {
            display: none;
          }
        }
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr 80px 80px;
        .message-count,
        .actions {
          display: none;
        }
      }

      .column-header {
        font-size: 0.75rem;
        font-weight: 700;
        color: $text-secondary;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }

  // Loading state
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    color: $text-secondary;

    .spinner {
      width: 2rem;
      height: 2rem;
      margin-bottom: 1rem;
      animation: spin 1s linear infinite;

      .path {
        stroke: $primary-color;
        stroke-linecap: round;
        animation: dash 1.5s ease-in-out infinite;
      }
    }

    p {
      font-size: 0.875rem;
      margin: 0;
    }
  }

  // Empty state
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    color: $text-muted;

    .empty-icon {
      width: 4rem;
      height: 4rem;
      margin-bottom: 1.5rem;
      color: $text-light;

      svg {
        width: 100%;
        height: 100%;
      }
    }

    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      color: $text-secondary;
      margin: 0 0 0.5rem 0;
    }

    p {
      font-size: 0.875rem;
      margin: 0;
    }
  }

  // List body
  .list-body {
    flex: 1;
    overflow-y: auto;

    .conversation-row {
      display: grid;
      grid-template-columns: 200px 1fr 80px 120px 80px;
      gap: 0.75rem;
      align-items: center;
      padding: 0.75rem;
      border-bottom: 1px solid $border-color;
      cursor: pointer;
      transition: $transition;

      &.with-recipient {
        grid-template-columns: 240px 1fr 70px 110px 70px;

        @media (max-width: 1024px) {
          grid-template-columns: 200px 1fr 60px;
          .message-count,
          .actions {
            display: none;
          }
        }

        @media (max-width: 768px) {
          grid-template-columns: 1fr 80px;
          .message-count,
          .actions {
            display: none;
          }
        }
      }

      @media (max-width: 768px) {
        grid-template-columns: 1fr 80px 80px;
        .message-count,
        .actions {
          display: none;
        }
      }

      &:hover {
        background-color: $bg-hover;
      }

      &.unread {
        background-color: rgba($primary-color, 0.05);
        border-left: 4px solid $primary-color;

        .subject-text {
          font-weight: 600;
        }
      }

      &:last-child {
        border-bottom: none;
      }

      .column-content {
        min-width: 0; // Allows text truncation
      }

      // Participants column (combined From/To)
      .participants {
        .participants-info {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;

          .participant-row {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            min-height: 1.5rem;

            .participant-label {
              font-size: 0.625rem;
              font-weight: 600;
              color: $text-muted;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              min-width: 2rem;
              flex-shrink: 0;
            }

            .participant-content {
              display: flex;
              align-items: center;
              gap: 0.375rem;
              flex: 1;
              min-width: 0;

              .participant-avatar {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 50%;
                color: white;
                font-size: 0.5rem;
                font-weight: 700;
                flex-shrink: 0;
              }

              .participant-name {
                font-size: 0.75rem;
                font-weight: 500;
                color: $text-primary;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                flex: 1;

                &:hover {
                  color: $primary-color;
                }
              }
            }

            &.to-row {
              .participant-label {
                color: $text-light;
              }
              .participant-name {
                font-size: 0.7rem;
                color: $text-secondary;
              }
            }
          }

          .participants-count {
            font-size: 0.625rem;
            color: $text-muted;
            margin-top: 0.125rem;
            padding-left: 2.5rem;
          }
        }

        // Mobile optimizations
        @media (max-width: 768px) {
          .participants-info {
            .participant-row {
              .participant-label {
                display: none;
              }

              .participant-content {
                .participant-avatar {
                  width: 1.25rem;
                  height: 1.25rem;
                  font-size: 0.4rem;
                }

                .participant-name {
                  font-size: 0.7rem;
                }
              }
            }
          }
        }
      }

      // Subject column
      .subject {
        .subject-line {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.25rem;

          .subject-text {
            font-size: 0.875rem;
            color: $text-primary;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            flex: 1;
          }

          .subject-meta {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex-shrink: 0;

            .unread-indicator {
              font-size: 0.625rem;
              font-weight: 600;
              color: $primary-color;
              background: rgba($primary-color, 0.1);
              padding: 0.125rem 0.375rem;
              border-radius: 0.75rem;
            }

            .attachment-indicator {
              color: $text-muted;

              svg {
                width: 0.875rem;
                height: 0.875rem;
              }
            }
          }
        }

        .latest-content {
          font-size: 0.75rem;
          color: $text-muted;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }

      // Message count column
      .message-count {
        text-align: center;

        .count-badge {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 1.5rem;
          height: 1.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          color: $text-secondary;
          background: $bg-light;
          border-radius: 0.75rem;
          padding: 0 0.375rem;
        }
      }

      // Date column
      .date {
        text-align: right;

        .date-info {
          .date-text {
            font-size: 0.75rem;
            font-weight: 500;
            color: $text-secondary;
          }

          .time-text {
            font-size: 0.625rem;
            color: $text-muted;
            margin-top: 0.125rem;
          }
        }
      }

      // Actions column
      .actions {
        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
          opacity: 0.7;
          transition: $transition;

          .action-button {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2rem;
            height: 2rem;
            background: transparent;
            border: 1px solid $border-color;
            border-radius: $radius;
            color: $text-muted;
            cursor: pointer;
            transition: $transition;
            padding: 0;

            &:hover {
              background: $bg-light;
              border-color: $primary-color;
              color: $primary-color;
              transform: scale(1.05);
            }

            &.delete-button:hover {
              background: rgba($error-color, 0.1);
              border-color: $error-color;
              color: $error-color;
            }

            svg {
              width: 1rem;
              height: 1rem;
              stroke-width: 2;
            }
          }
        }
      }

      &:hover .action-buttons {
        opacity: 1;
      }
    }
  }
}

// Animations
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}
</style>
